import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, Loader2, SlidersHorizontal, CheckCircle2, XCircle } from "lucide-react";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

const ROLE_LABELS = {
  admin: "Admin",
  partner: "Partner",
  user: "Hitch Zone User",
  approver_l1: "Approver L1",
  approver_l2: "Approver L2",
  approver_l3: "Approver L3",
  approver_all: "Approver (All Levels)",
};

export default function AdminFeatureMatrix() {
  const [matrix, setMatrix] = useState(null); // { role: { key: {enabled, limit} } }
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleMeta, setRoleMeta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyCell, setBusyCell] = useState(null); // `${role}:${key}`
  const [audit, setAudit] = useState([]);
  const [sideFilter, setSideFilter] = useState("all"); // all | admin | partner

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature-matrix");
      setMatrix(data.matrix || {});
      setCatalog(data.catalog || []);
      setRoles(data.roles || []);
      setRoleMeta(data.role_meta || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load matrix");
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/admin/feature-matrix/audit?limit=100");
      setAudit(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load audit");
    }
  };

  useEffect(() => { load(); loadAudit(); }, []);

  // Filter catalog and roles based on side filter
  const filteredCatalog = useMemo(() => {
    if (sideFilter === "all") return catalog;
    return catalog.filter((f) => f.side === sideFilter || f.side === "both");
  }, [catalog, sideFilter]);

  const filteredRoles = useMemo(() => {
    if (sideFilter === "all") return roles;
    return roles.filter((r) => {
      const meta = roleMeta.find((m) => m.slug === r);
      return meta ? meta.side === sideFilter : true;
    });
  }, [roles, roleMeta, sideFilter]);

  const grouped = useMemo(() => {
    const g = {};
    for (const f of filteredCatalog) {
      (g[f.group] = g[f.group] || []).push(f);
    }
    return g;
  }, [filteredCatalog]);

  const toggleCell = async (role, featureKey, next) => {
    setBusyCell(`${role}:${featureKey}`);
    try {
      await api.patch("/admin/feature-matrix", { role, feature_key: featureKey, enabled: next });
      setMatrix((m) => ({
        ...m,
        [role]: { ...(m?.[role] || {}), [featureKey]: { ...(m?.[role]?.[featureKey] || {}), enabled: next } },
      }));
      toast.success(`${ROLE_LABELS[role] || role} · ${featureKey} → ${next ? "ON" : "OFF"}`);
      loadAudit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusyCell(null);
    }
  };

  const saveLimit = async (role, featureKey, rawValue) => {
    const value = parseFloat(rawValue);
    if (Number.isNaN(value) || value < 0) { toast.error("Enter a valid positive number"); return; }
    setBusyCell(`${role}:${featureKey}`);
    try {
      await api.patch("/admin/feature-matrix", { role, feature_key: featureKey, limit: value });
      setMatrix((m) => ({
        ...m,
        [role]: { ...(m?.[role] || {}), [featureKey]: { ...(m?.[role]?.[featureKey] || {}), limit: value } },
      }));
      toast.success(`Limit saved · ${value.toLocaleString("en-IN")}`);
      loadAudit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusyCell(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Functional Control</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
            <SlidersHorizontal className="h-7 w-7 text-primary" strokeWidth={1.5} /> Feature matrix
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Enable or disable every module, payout rail, BBPS category, gateway, and numeric limit per role. Disabled items are hidden from the UI entirely for that role.
          </p>
        </div>
      </div>

      <Tabs defaultValue="matrix" className="mt-8">
        <TabsList>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Matrix</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="h-3.5 w-3.5 mr-1" /> Audit log ({audit.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="mt-6">
          {/* Side filter */}
          <div className="flex items-center gap-2 mb-4" data-testid="side-filter">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Show:</span>
            {[
              { value: "all", label: "All features" },
              { value: "admin", label: "Admin" },
              { value: "partner", label: "Partner" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSideFilter(opt.value)}
                data-testid={`filter-${opt.value}`}
                className={[
                  "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                  sideFilter === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent",
                ].join(" ")}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {loading || !matrix ? (
            <div className="surface-card p-10 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            Object.entries(grouped).map(([group, feats]) => (
              <div key={group} className="surface-card overflow-hidden mb-6" data-testid={`group-${group.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold">{group}</h3>
                  <span className="text-xs text-muted-foreground font-mono">{feats.length} features</span>
                </div>
                <div className="overflow-x-auto relative">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="p-3 pl-5 min-w-[200px] sticky left-0 z-10 bg-card" style={{ boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)" }}>Feature</th>
                        {filteredRoles.map((r) => (
                          <th key={r} className="p-3 text-center min-w-[120px]">{ROLE_LABELS[r] || r}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {feats.map((feat) => (
                        <tr key={feat.key} className="border-b border-border/50 last:border-0 hover:bg-accent/30">
                          <td className="p-3 pl-5 sticky left-0 z-10 bg-card" style={{ boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)" }}>
                            <div className="font-medium">{feat.label}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{feat.key}</div>
                          </td>
                          {filteredRoles.map((role) => {
                            const cell = matrix?.[role]?.[feat.key] || { enabled: false, limit: null };
                            const busyKey = `${role}:${feat.key}`;
                            if (feat.has_limit) {
                              return (
                                <td key={role} className="p-3 text-center">
                                  <LimitCell
                                    value={cell.limit}
                                    enabled={cell.enabled}
                                    busy={busyCell === busyKey}
                                    onToggle={(next) => toggleCell(role, feat.key, next)}
                                    onSave={(v) => saveLimit(role, feat.key, v)}
                                    testId={`cell-${role}-${feat.key}`}
                                  />
                                </td>
                              );
                            }
                            return (
                              <td key={role} className="p-3 text-center">
                                <div className="inline-flex items-center gap-2 justify-center">
                                  <Switch
                                    data-testid={`cell-${role}-${feat.key}`}
                                    checked={cell.enabled}
                                    disabled={busyCell === busyKey}
                                    onCheckedChange={(next) => toggleCell(role, feat.key, next)}
                                  />
                                  {busyCell === busyKey && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="surface-card overflow-hidden">
            <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Recent changes</h3>
              <Button size="sm" variant="outline" onClick={loadAudit} data-testid="refresh-audit">Refresh</Button>
            </div>
            {audit.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">No changes yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="p-3 pl-5">When</th>
                    <th className="p-3">Actor</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Feature</th>
                    <th className="p-3">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="p-3 pl-5 text-xs font-mono text-muted-foreground whitespace-nowrap">{formatTime(a.created_at)}</td>
                      <td className="p-3">
                        <div className="font-medium">{a.actor_full_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">{a.actor_email}</div>
                      </td>
                      <td className="p-3"><Badge variant="outline">{ROLE_LABELS[a.role] || a.role}</Badge></td>
                      <td className="p-3 font-mono text-xs">{a.feature_key}</td>
                      <td className="p-3">
                        {a.changes?.enabled && (
                          <span className="inline-flex items-center gap-1 text-xs">
                            {a.changes.enabled.to ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                            {a.changes.enabled.from ? "ON" : "OFF"} → <strong>{a.changes.enabled.to ? "ON" : "OFF"}</strong>
                          </span>
                        )}
                        {a.changes?.limit && (
                          <span className="inline-flex items-center gap-1 text-xs ml-2 font-mono">
                            limit {a.changes.limit.from ?? "—"} → <strong>{a.changes.limit.to}</strong>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function LimitCell({ value, enabled, busy, onToggle, onSave, testId }) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const changed = String(draft) !== String(value ?? "");
  return (
    <div className="inline-flex items-center gap-2 justify-center">
      <Switch
        data-testid={`${testId}-switch`}
        checked={enabled}
        disabled={busy}
        onCheckedChange={(next) => onToggle(next)}
      />
      <Input
        data-testid={testId}
        type="number"
        min="0"
        step="any"
        value={draft}
        disabled={!enabled || busy}
        onChange={(e) => setDraft(e.target.value)}
        className="w-24 h-8 font-mono text-xs"
        placeholder="—"
      />
      {changed && (
        <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => onSave(draft)} disabled={busy} data-testid={`${testId}-save`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
        </Button>
      )}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
